import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useJWTAuth } from '../components/JWTAuthContext';
import { createRazorpayOrder, verifyRazorpayPayment } from '../components/apiClient';
import '../styles/Payment.css';

// Razorpay types
declare global {
  interface Window {
    Razorpay: any;
  }
}

interface Plan {
  id: string;
  plan_key: string;
  plan_name: string;
  plan_description: string;
  monthly_price_inr: string;
  academic_session_price_inr: string;
  plan_color: string;
}

interface RazorpayResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

const Payment: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useJWTAuth();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Get plan and billing cycle from navigation state
  const plan: Plan | null = location.state?.plan || null;
  const billingCycle: 'monthly' | 'academic' = location.state?.billingCycle || 'monthly';

  useEffect(() => {
    // Redirect if not authenticated
    if (!isAuthenticated) {
      navigate('/login', { state: { from: location } });
      return;
    }

    // Redirect if no plan selected
    if (!plan) {
      navigate('/pricing');
      return;
    }

    // Load Razorpay script
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [isAuthenticated, plan, navigate, location]);

  const getAmount = () => {
    if (!plan) return 0;
    return billingCycle === 'monthly' 
      ? parseFloat(plan.monthly_price_inr)
      : parseFloat(plan.academic_session_price_inr);
  };

  const getTaxAmount = () => {
    return Math.round(getAmount() * 0.18 * 100) / 100; // 18% GST
  };

  const getTotalAmount = () => {
    return getAmount() ; //+ getTaxAmount();
  };

  const handleCreateOrder = async () => {
    try {
      const response = await createRazorpayOrder(plan?.id || '', billingCycle);
      
      if (response.status === 'success') {
        return response.result;
      } else {
        throw new Error(response.message || 'Failed to create order');
      }
    } catch (err: any) {
      throw new Error(err.message || 'Failed to create payment order');
    }
  };

  const handleVerifyPayment = async (paymentData: RazorpayResponse) => {
    try {
      const response = await verifyRazorpayPayment(
        paymentData.razorpay_order_id,
        paymentData.razorpay_payment_id,
        paymentData.razorpay_signature
      );
      
      if (response.status === 'success') {
        return response.result;
      } else {
        throw new Error(response.message || 'Payment verification failed');
      }
    } catch (err: any) {
      throw new Error(err.message || 'Failed to verify payment');
    }
  };

  const handlePayment = async () => {
    if (!plan || !user) return;

    setLoading(true);
    setError(null);

    try {
      // Create Razorpay order using apiClient
      const orderData = await handleCreateOrder();

      // Configure Razorpay options
      const options = {
        key: orderData.key_id,
        amount: orderData.amount * 100, // Convert to paise
        currency: orderData.currency,
        name: 'WiziAI',
        description: `${plan.plan_name} - ${billingCycle === 'monthly' ? 'Monthly' : 'Academic Session'}`,
        order_id: orderData.order_id,
        prefill: {
          name: orderData.user_name,
          email: orderData.user_email,
          contact: orderData.user_phone
        },
        theme: {
          color: plan.plan_color || '#667eea'
        },
        handler: async function (response: RazorpayResponse) {
          try {
            // Verify payment on backend using apiClient
            const verificationResult = await handleVerifyPayment(response);
            
            // Show success message
            alert('🎉 ' + verificationResult.message);
            
            // Redirect to dashboard
            setTimeout(() => {
              navigate('/dashboard');
            }, 1500);
            
          } catch (err: any) {
            setError(err.message);
            setLoading(false);
          }
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
          }
        }
      };

      // Open Razorpay checkout
      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (err: any) {
      console.error('Payment error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  if (!plan) {
    return null;
  }

  return (
    <div className="payment-container">
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            {/* Payment Header */}
            <div className="payment-header text-center mb-5">
              <h1 className="display-5 fw-bold mb-3">Complete Your Payment</h1>
              <p className="lead text-muted">You're one step away from unlocking premium features</p>
            </div>

            <div className="row g-4">
              {/* Plan Details Card */}
              <div className="col-md-6">
                <div className="card plan-summary-card h-100">
                  <div 
                    className="card-header text-white"
                    style={{ backgroundColor: plan.plan_color }}
                  >
                    <h5 className="mb-0">
                      <i className="fas fa-crown me-2"></i>
                      Plan Details
                    </h5>
                  </div>
                  <div className="card-body">
                    <h3 className="plan-name mb-3" style={{ color: plan.plan_color }}>
                      {plan.plan_name}
                    </h3>
                    <p className="text-muted mb-4">{plan.plan_description}</p>
                    
                    <div className="billing-info mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="text-muted">Billing Cycle:</span>
                        <span className="fw-bold">
                          {billingCycle === 'monthly' ? 'Monthly' : 'Academic Session'}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted">Auto-Renewal:</span>
                        <span className="badge bg-success">
                          {billingCycle === 'monthly' ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>

                    <div className="features-preview mt-4">
                      <h6 className="fw-bold mb-3">What you'll get:</h6>
                      <ul className="list-unstyled">
                        <li className="mb-2">
                          <i className="fas fa-check-circle text-success me-2"></i>
                          All premium features
                        </li>
                        <li className="mb-2">
                          <i className="fas fa-check-circle text-success me-2"></i>
                          AI-powered performance analysis
                        </li>
                        <li className="mb-2">
                          <i className="fas fa-check-circle text-success me-2"></i>
                          Unlimited practice tests
                        </li>
                        <li className="mb-2">
                          <i className="fas fa-check-circle text-success me-2"></i>
                          24/7 customer support
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Summary Card */}
              <div className="col-md-6">
                <div className="card payment-summary-card h-100">
                  <div className="card-header bg-primary text-white">
                    <h5 className="mb-0">
                      <i className="fas fa-receipt me-2"></i>
                      Payment Summary
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="amount-breakdown">
                      <div className="d-flex justify-content-between mb-3 pb-3 border-bottom">
                        <span>Plan Amount:</span>
                        <span className="fw-bold">₹{getAmount().toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                      {/* <div className="d-flex justify-content-between mb-3 pb-3 border-bottom">
                        <span>GST (18%):</span>
                        <span className="fw-bold">₹{getTaxAmount().toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div> */}
                      <div className="d-flex justify-content-between mb-4 pb-3 border-bottom">
                        <span className="fs-5 fw-bold">Total Amount:</span>
                        <span className="fs-4 fw-bold text-primary">
                          ₹{getTotalAmount().toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>

                    {error && (
                      <div className="alert alert-danger mb-3" role="alert">
                        <i className="fas fa-exclamation-circle me-2"></i>
                        {error}
                      </div>
                    )}

                    <button
                      className="btn btn-primary btn-lg w-100 mb-3"
                      onClick={handlePayment}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Processing...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-lock me-2"></i>
                          Pay Securely
                        </>
                      )}
                    </button>

                    <div className="payment-security text-center">
                      <small className="text-muted">
                        <i className="fas fa-shield-alt me-1"></i>
                        Secured by Razorpay | Your payment information is encrypted
                      </small>
                    </div>

                    <div className="accepted-payments mt-4 text-center">
                      <small className="text-muted d-block mb-2">We accept:</small>
                      <div className="d-flex justify-content-center gap-2">
                        <span className="badge bg-light text-dark">💳 Card</span>
                        <span className="badge bg-light text-dark">📱 UPI</span>
                        <span className="badge bg-light text-dark">🏦 Net Banking</span>
                        <span className="badge bg-light text-dark">💼 Wallet</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-3 text-center">
                  <button
                    className="btn btn-link text-muted"
                    onClick={() => navigate('/pricing')}
                  >
                    <i className="fas fa-arrow-left me-2"></i>
                    Back to Plans
                  </button>
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="card mt-4 border-0 bg-light">
              <div className="card-body">
                <div className="row text-center">
                  <div className="col-md-4 mb-3 mb-md-0">
                    <i className="fas fa-undo text-primary fs-3 mb-2"></i>
                    <h6 className="fw-bold">100% Satisfaction</h6>
                    <small className="text-muted">If you're not satisfied, let us know!</small>
                  </div>
                  <div className="col-md-4 mb-3 mb-md-0">
                    <i className="fas fa-headset text-primary fs-3 mb-2"></i>
                    <h6 className="fw-bold">24/7 Support</h6>
                    <small className="text-muted">We're here to help</small>
                  </div>
                  <div className="col-md-4">
                    <i className="fas fa-lock text-primary fs-3 mb-2"></i>
                    <h6 className="fw-bold">Secure Payment</h6>
                    <small className="text-muted">SSL encrypted checkout</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payment;
